import { SignInIdentifier } from '@logto/schemas';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import api from '@/apis/api';
import { experienceApiRoutes } from '@/apis/experience/const';
import SecondaryPageLayout from '@/Layout/SecondaryPageLayout';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import { useSieMethods } from '@/hooks/use-sie';
import ErrorPage from '@/pages/ErrorPage';
import { UserFlow } from '@/types';
import { formatPhoneNumberWithCountryCallingCode } from '@/utils/country-code';
import { identifierInputDescriptionMap } from '@/utils/form';
import { codeVerificationTypeMap } from '@/utils/sign-in-experience';

import PasswordForm from './PasswordForm';

const SignInPassword = () => {
  const { t } = useTranslation();
  const { signInMethods } = useSieMethods();

  const { identifierInputValue, verificationIdsMap } = useContext(UserInteractionContext);
  const [userHasPassword, setUserHasPassword] = useState<boolean | undefined>(undefined);
  const [isChecking, setIsChecking] = useState(true);

  if (!identifierInputValue) {
    return <ErrorPage title="error.invalid_session" />;
  }

  const { type, value } = identifierInputValue;

  const methodSetting = signInMethods.find((method) => method.identifier === type);

  // Sign-in method not enabled
  if (!methodSetting?.password) {
    return <ErrorPage />;
  }

  // Check if user has password for email identifier
  useEffect(() => {
    if (type === SignInIdentifier.Email) {
      const checkPassword = async () => {
        setIsChecking(true);
        try {
          const response = await api.get(`${experienceApiRoutes.prefix}/check-password-by-email`, {
            searchParams: { email: value },
          });
          const result = await response.json<{ hasPassword: boolean }>();
          setUserHasPassword(result.hasPassword);
        } catch (error) {
          // If error, assume password exists (fallback to default behavior)
          console.error('Failed to check password:', error);
          setUserHasPassword(true);
        } finally {
          setIsChecking(false);
        }
      };
      void checkPassword();
    } else {
      // For non-email, assume password exists
      setUserHasPassword(true);
      setIsChecking(false);
    }
  }, [type, value]);

  // If user doesn't have password and verification code is enabled, redirect to verification code
  if (!isChecking && userHasPassword === false && methodSetting.verificationCode) {
    let verificationId: string | undefined;
    if (type === SignInIdentifier.Email) {
      verificationId = verificationIdsMap[codeVerificationTypeMap[SignInIdentifier.Email]];
    } else if (type === SignInIdentifier.Phone) {
      verificationId = verificationIdsMap[codeVerificationTypeMap[SignInIdentifier.Phone]];
    }
    if (verificationId) {
      return <Navigate replace to={`/${UserFlow.SignIn}/verification-code`} />;
    }
  }

  return (
    <SecondaryPageLayout
      title="description.enter_password"
      description="description.enter_password_for"
      descriptionProps={{
        method: t(identifierInputDescriptionMap[methodSetting.identifier]),
        value:
          methodSetting.identifier === SignInIdentifier.Phone
            ? formatPhoneNumberWithCountryCallingCode(value)
            : value,
      }}
    >
      <PasswordForm
        autoFocus
        identifier={methodSetting.identifier}
        value={value}
        isVerificationCodeEnabled={methodSetting.verificationCode}
      />
    </SecondaryPageLayout>
  );
};

export default SignInPassword;
